import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { getAuthInfo } from '@/utils/auth'
import { createTransactionId } from '@/utils/transactionId'
import { formatRewardAmountDisplay } from '@/utils/rewardDisplay'
import {
  getMemberDashboard,
  getRewardPolicy,
  getRewardRecent,
  membershipCodesFromPolicy,
  postRewardsCancelEarnQuantity,
  postRewardsCancelUseQuantity,
  postRewardsEarn,
  postRewardsUse
} from '@/api/member'
import { getRecentCouponHistoryTop5, getUnusedCouponsTop5, postCouponUse } from '@/api/coupon'
import type { CouponItemDto } from '@/api/coupon'
import type {
  MemberCustomerStatus,
  MemberDashboardState,
  MemberProfile,
  MemberSearchStatus
} from '@/types/member'
import type { Membership, MembershipCodes, RewardRecentItem } from '@/api/member'
import styles from './MemberDashboard.module.css'

const REWARD_STATUS_LABEL: Record<RewardRecentItem['rewardStatus'], string> = {
  EARN: '적립',
  EARN_CANCEL: '적립취소',
  USE: '사용',
  USE_CANCEL: '사용취소',
  EXPIRE: '만료'
}

/** 멤버십 미로딩·오류 시 카드용 안내 */
const REWARD_CONDITION_FALLBACK =
  '• 멤버십 정책을 불러오면 적립 조건이 표시됩니다.'

const BLOCKED_MEMBER_MESSAGE = '차단된 회원입니다. 적립·사용이 불가합니다.'

function membershipRecord(m: Membership): Record<string, unknown> {
  return m as unknown as Record<string, unknown>
}

/** API가 0/1을 문자열로 주는 경우 대응 */
function normalizeValidType(v: unknown): 0 | 1 | null {
  if (v === 0 || v === '0') return 0
  if (v === 1 || v === '1') return 1
  const n = Number(v)
  if (n === 0) return 0
  if (n === 1) return 1
  return null
}

function pickStr(r: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const k of keys) {
    const v = r[k]
    if (v == null) continue
    if (typeof v === 'string' && v.trim() !== '') return v
  }
  return undefined
}

function pickNum(r: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const k of keys) {
    const v = r[k]
    if (v == null) continue
    const n = typeof v === 'number' ? v : Number(v)
    if (!Number.isNaN(n)) return n
  }
  return undefined
}

/** 불릿 없음 — 하위 한 줄 조합용 */
function validityText(m: Membership): string | null {
  const r = membershipRecord(m)
  const t = normalizeValidType(r.validType ?? r['valid_type'])
  const period = pickNum(r, 'validPeriod', 'valid_period')
  const at = pickStr(r, 'validAt', 'valid_at')
  if (t === 0 && period != null) return `유효기간 : ${period}일`
  if (t === 1 && at != null) return `유효기간 : ${at}`
  return null
}

function pointRewardTargetLabel(target: string | undefined): string {
  if (target === 'payment') return '결제'
  if (target === 'order') return '주문'
  return '결제+주문'
}

const SUB_LINE_INDENT = '  '

/** reward-policy 멤버십 한 건 → 적립 조건 줄 배열 (타이틀은 줄마다, 하위는 한 줄) */
function linesForMembership(m: Membership): string[] {
  const out: string[] = []
  const r = membershipRecord(m)
  const stamp = m.rewardType === 'STAMP' || m.rewardType === 'BOTH'
  const point = m.rewardType === 'POINT' || m.rewardType === 'BOTH'

  if (stamp) {
    const rewardTitle = pickStr(r, 'rewardTitle', 'reward_title')
    const subs: string[] = []
    const setCount = pickNum(r, 'stampSetCount', 'stamp_set_count') ?? m.stampSetCount
    if (setCount != null) subs.push(`스탬프/세트 : ${setCount}개`)
    const sv = validityText(m)
    if (sv) subs.push(sv)
    if (rewardTitle) out.push(`• ${rewardTitle}`)
    if (subs.length > 0) {
      const joined = subs.join(' · ')
      out.push(rewardTitle ? `${SUB_LINE_INDENT}${joined}` : `• ${joined}`)
    }
  }

  if (point) {
    const rewardTitle = pickStr(r, 'rewardTitle', 'reward_title')
    const subs: string[] = []
    const ratio = pickNum(r, 'pointRatio', 'point_ratio') ?? m.pointRatio
    if (ratio != null) subs.push(`적립률 : ${ratio}%`)
    const pv = validityText(m)
    if (pv) subs.push(pv)
    const target = pickStr(r, 'pointRewardTarget', 'point_reward_target') ?? m.pointRewardTarget
    if (target != null && target !== '') subs.push(`적립대상 : ${pointRewardTargetLabel(target)}`)
    if (rewardTitle) out.push(`• ${rewardTitle}`)
    if (subs.length > 0) {
      const joined = subs.join(' · ')
      out.push(rewardTitle ? `${SUB_LINE_INDENT}${joined}` : `• ${joined}`)
    }
  }

  if (out.length === 0) {
    out.push(`• ${m.title}`)
  }
  return out
}

function rewardConditionsFromMemberships(list: Membership[] | null): string {
  if (list == null || list.length === 0) return ''
  return list
    .map((m) => [m.title, ...linesForMembership(m)].join('\n'))
    .join('\n\n')
}

/** 미사용 쿠폰 한 건 */
interface UnusedCoupon {
  id: string
  code: string
  name: string
  issuedAt: string
  expiresAt: string
  remainingDays: number
}

/** 쿠폰 내역 한 건 */
interface CouponHistoryItem {
  id: string
  date: string
  description: string
  status: 'issued' | 'used'
}

function formatYmdDot(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}.${m}.${day}`
}

function calcRemainingDays(expireAtIso: string): number {
  const expireAt = new Date(expireAtIso).getTime()
  if (Number.isNaN(expireAt)) return 0
  const now = Date.now()
  const diffDays = Math.ceil((expireAt - now) / (1000 * 60 * 60 * 24))
  return Math.max(0, diffDays)
}

function toUnusedCoupon(dto: CouponItemDto): UnusedCoupon {
  // B안(타임스탬프 비교) 방어: status가 아직 VALID/PENDING이어도 만료 시각이 지났으면 미사용 목록에서 제외
  // (백엔드가 KST 23:59:59.999로 만료 시각을 세팅한다는 전제)
  const expireAtMs = new Date(dto.expireAt).getTime()
  const isExpiredByTime =
    !Number.isNaN(expireAtMs) &&
    Date.now() > expireAtMs &&
    dto.status !== 'USED' &&
    dto.status !== 'CANCELED' &&
    dto.status !== 'SUSPENDED'

  return {
    id: dto.couponCode,
    code: dto.couponCode,
    name: dto.couponName,
    issuedAt: formatYmdDot(dto.issuedAt),
    expiresAt: formatYmdDot(dto.expireAt),
    remainingDays: isExpiredByTime ? 0 : calcRemainingDays(dto.expireAt)
  }
}

function toCouponHistoryItem(dto: CouponItemDto): CouponHistoryItem {
  return {
    id: dto.couponCode,
    date: formatYmdDot(dto.issuedAt),
    description: dto.couponName,
    status: dto.status === 'USED' ? 'used' : 'issued'
  }
}

export default function MemberDashboard() {
  const navigate = useNavigate()
  const location = useLocation()

  const [member, setMember] = useState<MemberProfile | null>(null)
  const [searchStatus, setSearchStatus] = useState<MemberSearchStatus | null>(null)
  const [customerStatus, setCustomerStatus] = useState<MemberCustomerStatus | null>(null)
  const [memberLoading, setMemberLoading] = useState(false)
  const [unusedCoupons, setUnusedCoupons] = useState<UnusedCoupon[]>([])
  const [couponHistory, setCouponHistory] = useState<CouponHistoryItem[]>([])

  const [memberships, setMemberships] = useState<Membership[] | null>(null)
  const [membershipCodes, setMembershipCodes] = useState<MembershipCodes>({})
  const [membershipsLoading, setMembershipsLoading] = useState(false)
  const [membershipsError, setMembershipsError] = useState<string | null>(null)
  const [rewardRecent, setRewardRecent] = useState<RewardRecentItem[]>([])
  const [rewardRecentLoading, setRewardRecentLoading] = useState(false)
  const [rewardRecentError, setRewardRecentError] = useState<string | null>(null)

  const [stampCount, setStampCount] = useState(1)
  const [paymentAmount, setPaymentAmount] = useState('')
  const [usePoints, setUsePoints] = useState('')
  /** 멤버십 카드별 적립/취소 모드 */
  const [cardModes, setCardModes] = useState<Record<string, 'earn' | 'cancel'>>({})
  const [cancelStampCount, setCancelStampCount] = useState(1)
  const [cancelPoints, setCancelPoints] = useState('')

  const [stampEarnLoading, setStampEarnLoading] = useState(false)
  const [pointEarnLoading, setPointEarnLoading] = useState(false)
  const [pointUseLoading, setPointUseLoading] = useState(false)
  const [cancelLoadingKey, setCancelLoadingKey] = useState<string | null>(null)
  const [usingCouponCode, setUsingCouponCode] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const pointsConditionText = useMemo(() => {
    if (membershipsLoading && (memberships == null || memberships.length === 0)) {
      return '• 멤버십 정책을 불러오는 중입니다.'
    }
    if (membershipsError && (memberships == null || memberships.length === 0)) {
      return '• 멤버십 정책을 불러오지 못했습니다.'
    }
    const built = rewardConditionsFromMemberships(memberships)
    if (built !== '') return built
    if (memberships != null && memberships.length === 0) {
      return '• 적용 중인 멤버십이 없습니다.'
    }
    return REWARD_CONDITION_FALLBACK
  }, [memberships, membershipsLoading, membershipsError])

  const isBlocked = customerStatus === 'BLOCKED'

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type })
    const t = setTimeout(() => setToast(null), 3000)
    return () => clearTimeout(t)
  }, [])

  const goBack = () => {
    navigate('/dashboard')
  }

  const handlePaymentAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '')
    setPaymentAmount(raw ? parseInt(raw, 10).toLocaleString() : '')
  }

  const handleUsePointsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '')
    setUsePoints(raw ? parseInt(raw, 10).toLocaleString() : '')
  }

  const increaseStamp = () => {
    setStampCount((c) => (c < 10 ? c + 1 : c))
  }

  const decreaseStamp = () => {
    setStampCount((c) => (c > 1 ? c - 1 : c))
  }

  const setCardMode = (membershipCode: string, mode: 'earn' | 'cancel') => {
    setCardModes((prev) => ({ ...prev, [membershipCode]: mode }))
    if (mode === 'cancel') {
      setCancelStampCount(1)
      setCancelPoints('')
    }
  }

  const increaseCancelStamp = () => {
    const max = Math.max(1, member?.stampCount ?? 1)
    setCancelStampCount((c) => (c < max ? c + 1 : c))
  }

  const decreaseCancelStamp = () => {
    setCancelStampCount((c) => (c > 1 ? c - 1 : c))
  }

  const handleCancelPointsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '')
    setCancelPoints(raw ? parseInt(raw, 10).toLocaleString() : '')
  }

  const refreshAfterRewardChange = async (
    projectCode: string,
    shopCode: string,
    customerCode: string,
    brandCode: string,
    rewardType: 'STAMP' | 'POINT',
    balance: number
  ) => {
    if (rewardType === 'STAMP') {
      setMember((m) => (m ? { ...m, stampCount: balance } : null))
    } else {
      setMember((m) => (m ? { ...m, pointAmount: balance } : null))
    }
    try {
      const dash = await getMemberDashboard(projectCode, shopCode, customerCode, membershipCodes)
      setMember((m) =>
        m
          ? {
              ...m,
              stampCount: dash.stampCount,
              pointAmount: dash.pointAmount,
              couponCount: dash.couponCount
            }
          : null
      )
      if (dash.searchStatus !== searchStatus) {
        setSearchStatus(dash.searchStatus)
      }
    } catch {
      // earn/cancel 응답 balance만 유지
    }
    const recent = await getRewardRecent(projectCode, shopCode, customerCode, brandCode, 5)
    setRewardRecent(recent)
  }

  const cancelRewardQuantity = async (
    membershipCode: string,
    rewardType: 'STAMP' | 'POINT',
    action: 'earn' | 'use'
  ) => {
    if (!member) return
    if (customerStatus === 'BLOCKED') {
      showToast(BLOCKED_MEMBER_MESSAGE, 'error')
      return
    }

    const quantity =
      rewardType === 'STAMP'
        ? cancelStampCount
        : parseInt(cancelPoints.replace(/,/g, ''), 10) || 0

    if (quantity < 1) {
      showToast(
        rewardType === 'STAMP' ? '1개 이상 입력해주세요.' : '1P 이상 입력해주세요.',
        'error'
      )
      return
    }

    if (action === 'earn') {
      const balance = rewardType === 'STAMP' ? member.stampCount : member.pointAmount
      if (quantity > balance) {
        showToast(
          rewardType === 'STAMP' ? '보유 스탬프가 부족합니다.' : '보유 포인트가 부족합니다.',
          'error'
        )
        return
      }
    }

    const typeLabel = rewardType === 'STAMP' ? '스탬프' : '포인트'
    const actionLabel = action === 'earn' ? '적립취소' : '사용취소'
    const qtyLabel =
      rewardType === 'STAMP' ? `${quantity}개` : `${quantity.toLocaleString()}P`
    if (!window.confirm(`${typeLabel} ${qtyLabel} ${actionLabel}할까요?`)) return

    const authInfo = getAuthInfo()
    const projectCode = authInfo?.projectCode?.trim()
    const shopCode = authInfo?.shopCode?.trim()
    const brandCode = authInfo?.brandCode?.trim()
    if (!projectCode || !shopCode || !brandCode) {
      showToast('매장/브랜드 정보가 없습니다. 다시 로그인해 주세요.', 'error')
      return
    }
    if (!membershipCode.trim()) {
      showToast('멤버십 정보가 없습니다.', 'error')
      return
    }

    const transactionId = createTransactionId()
    const loadingKey = `${membershipCode}-${rewardType}-${action}`
    setCancelLoadingKey(loadingKey)
    try {
      const body = {
        rewardType,
        brandCode,
        membershipCode,
        transactionId,
        quantity,
        ...(action === 'use' ? { orderId: transactionId } : {})
      }
      const data =
        action === 'earn'
          ? await postRewardsCancelEarnQuantity(
              projectCode,
              shopCode,
              member.customerCode,
              body
            )
          : await postRewardsCancelUseQuantity(
              projectCode,
              shopCode,
              member.customerCode,
              body
            )
      await refreshAfterRewardChange(
        projectCode,
        shopCode,
        member.customerCode,
        brandCode,
        rewardType,
        data.balance
      )
      showToast(`${typeLabel} ${qtyLabel} ${actionLabel}되었습니다.`, 'success')
      if (rewardType === 'STAMP') setCancelStampCount(1)
      else setCancelPoints('')
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : `${typeLabel} ${actionLabel}에 실패했습니다.`,
        'error'
      )
    } finally {
      setCancelLoadingKey(null)
    }
  }

  const earnStamp = async () => {
    if (!member) return
    if (customerStatus === 'BLOCKED') {
      showToast(BLOCKED_MEMBER_MESSAGE, 'error')
      return
    }
    if (stampCount < 1) {
      showToast('1개 이상 입력해주세요.', 'error')
      return
    }
    const authInfo = getAuthInfo()
    const projectCode = authInfo?.projectCode?.trim()
    const shopCode = authInfo?.shopCode?.trim()
    const brandCode = authInfo?.brandCode?.trim()
    const membershipCode = membershipCodes.stampMembershipCode?.trim()
    if (!projectCode || !shopCode || !brandCode) {
      showToast('매장/브랜드 정보가 없습니다. 다시 로그인해 주세요.', 'error')
      return
    }
    if (!membershipCode) {
      showToast('스탬프 멤버십 정보가 없습니다.', 'error')
      return
    }
    const transactionId = createTransactionId()
    const receiptAt = new Date().toISOString()
    setStampEarnLoading(true)
    try {
      const data = await postRewardsEarn(projectCode, shopCode, member.customerCode, {
        rewardType: 'STAMP',
        brandCode,
        membershipCode,
        transactionId,
        quantity: stampCount,
        orderId: transactionId,
        receiptNo: '',
        receiptAt
      })
      setMember((m) =>
        m
          ? {
              ...m,
              stampCount: data.balance
            }
          : null
      )
      try {
        const dash = await getMemberDashboard(
          projectCode,
          shopCode,
          member.customerCode,
          membershipCodes
        )
        setMember((m) =>
          m
            ? {
                ...m,
                stampCount: dash.stampCount,
                couponCount: dash.couponCount
              }
            : null
        )
        if (dash.searchStatus !== searchStatus) {
          setSearchStatus(dash.searchStatus)
        }
      } catch {
        // 대시보드 갱신 실패 시 earn 응답 balance만 반영
      }
      const recent = await getRewardRecent(projectCode, shopCode, member.customerCode, brandCode, 5)
      setRewardRecent(recent)
      showToast(`스탬프 ${data.quantity}개가 적립되었습니다.`, 'success')
      setStampCount(1)
    } catch (err) {
      showToast(err instanceof Error ? err.message : '스탬프 적립에 실패했습니다.', 'error')
    } finally {
      setStampEarnLoading(false)
    }
  }

  const earnPoints = async () => {
    if (customerStatus === 'BLOCKED') {
      showToast(BLOCKED_MEMBER_MESSAGE, 'error')
      return
    }
    const amountStr = paymentAmount.replace(/,/g, '')
    if (!amountStr || Number(amountStr) < 1000) {
      showToast('결제 금액을 1,000원 이상 입력해주세요.', 'error')
      return
    }
    if (!member) return
    const authInfo = getAuthInfo()
    const projectCode = authInfo?.projectCode?.trim()
    const shopCode = authInfo?.shopCode?.trim()
    const brandCode = authInfo?.brandCode?.trim()
    if (!projectCode || !shopCode || !brandCode) {
      showToast('매장/브랜드 정보가 없습니다. 다시 로그인해 주세요.', 'error')
      return
    }
    const paymentAmountNum = parseInt(amountStr, 10)
    const membershipCode = membershipCodes.pointMembershipCode?.trim()
    if (!membershipCode) {
      showToast('포인트 멤버십 정보가 없습니다.', 'error')
      return
    }
    const transactionId = createTransactionId()
    setPointEarnLoading(true)
    try {
      const data = await postRewardsEarn(projectCode, shopCode, member.customerCode, {
        rewardType: 'POINT',
        brandCode,
        membershipCode,
        transactionId,
        paymentAmount: paymentAmountNum
      })
      setMember((m) =>
        m ? { ...m, pointAmount: data.balance } : null
      )
      const recent = await getRewardRecent(projectCode, shopCode, member.customerCode, brandCode, 5)
      setRewardRecent(recent)
      showToast(`${data.quantity.toLocaleString()}P가 적립되었습니다.`, 'success')
      setPaymentAmount('')
    } catch (err) {
      showToast(err instanceof Error ? err.message : '포인트 적립에 실패했습니다.', 'error')
    } finally {
      setPointEarnLoading(false)
    }
  }

  const usePointsAction = async () => {
    if (customerStatus === 'BLOCKED') {
      showToast(BLOCKED_MEMBER_MESSAGE, 'error')
      return
    }
    const pointsStr = usePoints.replace(/,/g, '')
    if (!pointsStr || Number(pointsStr) < 1) {
      showToast('사용할 포인트를 1P 이상 입력해주세요.', 'error')
      return
    }
    if (!member) return
    const numPoints = parseInt(pointsStr, 10)
    if (numPoints > member.pointAmount) {
      showToast('보유 포인트가 부족합니다.', 'error')
      return
    }
    const authInfo = getAuthInfo()
    const projectCode = authInfo?.projectCode?.trim()
    const shopCode = authInfo?.shopCode?.trim()
    const brandCode = authInfo?.brandCode?.trim()
    if (!projectCode || !shopCode || !brandCode) {
      showToast('매장/브랜드 정보가 없습니다. 다시 로그인해 주세요.', 'error')
      return
    }
    const transactionId = createTransactionId()
    const membershipCode = membershipCodes.pointMembershipCode?.trim()
    if (!membershipCode) {
      showToast('포인트 멤버십 정보가 없습니다.', 'error')
      return
    }
    setPointUseLoading(true)
    try {
      const data = await postRewardsUse(projectCode, shopCode, member.customerCode, {
        rewardType: 'POINT',
        brandCode,
        membershipCode,
        transactionId,
        quantity: numPoints,
        orderId: transactionId
      })
      setMember((m) =>
        m ? { ...m, pointAmount: data.balance } : null
      )
      const recent = await getRewardRecent(projectCode, shopCode, member.customerCode, brandCode, 5)
      setRewardRecent(recent)
      showToast(`${data.quantity.toLocaleString()}P가 사용되었습니다.`, 'success')
      setUsePoints('')
    } catch (err) {
      showToast(err instanceof Error ? err.message : '포인트 사용에 실패했습니다.', 'error')
    } finally {
      setPointUseLoading(false)
    }
  }

  const useCoupon = async (code: string) => {
    if (customerStatus === 'BLOCKED') {
      showToast(BLOCKED_MEMBER_MESSAGE, 'error')
      return
    }
    if (!window.confirm('쿠폰을 사용하시겠습니까?')) return
    if (!member) return
    const authInfo = getAuthInfo()
    const projectCode = authInfo?.projectCode?.trim()
    const shopCode = authInfo?.shopCode?.trim()
    const brandCode = authInfo?.brandCode?.trim()
    if (!projectCode || !shopCode || !brandCode) {
      showToast('매장/브랜드 정보가 없습니다. 다시 로그인해 주세요.', 'error')
      return
    }
    const usedOrderNo = `AGT-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    setUsingCouponCode(code)
    try {
      const data = await postCouponUse(projectCode, shopCode, code, {
        brandCode,
        customerCode: member.customerCode,
        usedOrderNo
      })
      const couponName = unusedCoupons.find((c) => c.code === code)?.name ?? code
      setUnusedCoupons((prev) => prev.filter((c) => c.code !== code))
      try {
        const dash = await getMemberDashboard(
          projectCode,
          shopCode,
          member.customerCode,
          membershipCodes
        )
        setMember((m) => (m ? { ...m, couponCount: dash.couponCount } : null))
      } catch {
        setMember((m) =>
          m ? { ...m, couponCount: Math.max(0, m.couponCount - 1) } : null
        )
      }
      setCouponHistory((prev) => [
        {
          id: data.usedOrderNo,
          date: formatYmdDot(data.usedAt),
          description: `${couponName} (${code})`,
          status: 'used'
        },
        ...prev
      ])
      showToast('쿠폰이 사용되었습니다.', 'success')
    } catch (err) {
      showToast(err instanceof Error ? err.message : '쿠폰 사용에 실패했습니다.', 'error')
    } finally {
      setUsingCouponCode(null)
    }
  }

  const showMorePointHistory = () => {
    if (!member) return
    navigate('/dashboard/member/accumulation-history', {
      state: {
        customerCode: member.customerCode,
        nickname: member.nickname,
        member,
        searchStatus: searchStatus ?? undefined,
        status: customerStatus ?? undefined
      }
    })
  }

  const showMoreCouponHistory = () => {
    if (!member) return
    navigate('/dashboard/member/coupon-history', {
      state: {
        customerCode: member.customerCode,
        nickname: member.nickname,
        member,
        searchStatus: searchStatus ?? undefined,
        status: customerStatus ?? undefined
      }
    })
  }

  useEffect(() => {
    const state = location.state as MemberDashboardState | null
    let cancelled = false

    setUnusedCoupons([])
    setCouponHistory([])
    setMemberships(null)
    setMembershipCodes({})
    setMembershipsError(null)
    setRewardRecent([])
    setRewardRecentError(null)
    setCustomerStatus(state?.status ?? null)

    const loadMember = async () => {
      const customerCode =
        (typeof state?.customerCode === 'string' ? state.customerCode.trim() : '') ||
        (typeof state?.member?.customerCode === 'string' ? state.member.customerCode.trim() : '') ||
        ''

      const nicknameHint =
        (typeof state?.nickname === 'string' && state.nickname.trim() !== ''
          ? state.nickname.trim()
          : undefined) ??
        (typeof state?.member?.nickname === 'string' && state.member.nickname.trim() !== ''
          ? state.member.nickname.trim()
          : undefined)

      if (!customerCode) {
        if (state?.member != null) {
          setMember(state.member)
          setSearchStatus(state.searchStatus ?? null)
        } else {
          setMember(null)
          setSearchStatus(null)
        }
        return
      }

      const authInfo = getAuthInfo()
      const projectCode = authInfo?.projectCode?.trim()
      const shopCode = authInfo?.shopCode?.trim()
      const brandCode = authInfo?.brandCode?.trim()
      if (!projectCode || !shopCode || !brandCode) {
        if (!cancelled) {
          setMember(null)
          setSearchStatus(null)
          showToast('매장/브랜드 정보가 없습니다. 다시 로그인해 주세요.', 'error')
        }
        return
      }

      if (!cancelled) {
        setMemberLoading(true)
        setMembershipsLoading(true)
        setMembershipsError(null)
      }
      try {
        let codes: MembershipCodes = {}
        if (brandCode) {
          try {
            const policyMemberships = await getRewardPolicy(projectCode, brandCode)
            if (cancelled) return
            if (import.meta.env.DEV) {
              console.debug(
                '[reward-policy] memberships (원본 — 유효기간·타이틀 필드 확인용)',
                policyMemberships
              )
            }
            setMemberships(policyMemberships)
            codes = membershipCodesFromPolicy(policyMemberships)
            setMembershipCodes(codes)
          } catch (err) {
            if (cancelled) return
            setMembershipsError(
              err instanceof Error ? err.message : '멤버십 정보를 불러올 수 없습니다.'
            )
          }
        }

        const data = await getMemberDashboard(
          projectCode,
          shopCode,
          customerCode,
          codes
        )
        if (cancelled) return
        setMember({
          ...data,
          customerCode,
          nickname: nicknameHint ?? customerCode
        })
        setSearchStatus(data.searchStatus)
      } catch (err) {
        if (cancelled) return
        setMember(null)
        setSearchStatus(null)
        showToast(err instanceof Error ? err.message : '회원 정보를 불러오지 못했습니다.', 'error')
      } finally {
        if (!cancelled) {
          setMemberLoading(false)
          setMembershipsLoading(false)
        }
      }
    }

    void loadMember()
    return () => {
      cancelled = true
    }
  }, [location.state, showToast])

  useEffect(() => {
    if (!member) return
    const authInfo = getAuthInfo()
    const projectCode = authInfo?.projectCode?.trim()
    const shopCode = authInfo?.shopCode?.trim()
    const brandCode = authInfo?.brandCode?.trim()
    const customerCode = member.customerCode
    if (!projectCode || !shopCode || !customerCode) return

    let cancelled = false

    const load = async () => {
      setRewardRecentLoading(true)
      setRewardRecentError(null)

      if (memberships == null && brandCode) {
        setMembershipsLoading(true)
        setMembershipsError(null)
        try {
          const policyMemberships = await getRewardPolicy(projectCode, brandCode)
          if (cancelled) return
          if (import.meta.env.DEV) {
            console.debug(
              '[reward-policy] memberships (원본 — 유효기간·타이틀 필드 확인용)',
              policyMemberships
            )
          }
          setMemberships(policyMemberships)
          setMembershipCodes(membershipCodesFromPolicy(policyMemberships))
        } catch (e) {
          if (!cancelled) {
            setMembershipsError(
              e instanceof Error ? e.message : '멤버십 정보를 불러올 수 없습니다.'
            )
          }
        } finally {
          if (!cancelled) setMembershipsLoading(false)
        }
      }

      try {
        const [rewardRecentRes, unusedRes, couponHistoryRes] = await Promise.allSettled([
          brandCode ? getRewardRecent(projectCode, shopCode, customerCode, brandCode, 5) : Promise.reject(new Error('브랜드 정보 없음')),
          getUnusedCouponsTop5({ projectCode, shopCode, customerCode, issuedShopCode: shopCode }),
          getRecentCouponHistoryTop5({ projectCode, shopCode, customerCode, issuedShopCode: shopCode })
        ])
        if (cancelled) return
        if (rewardRecentRes.status === 'fulfilled') setRewardRecent(rewardRecentRes.value)
        else setRewardRecentError(rewardRecentRes.reason?.message ?? '적립 내역을 불러올 수 없습니다.')
        if (unusedRes.status === 'fulfilled') {
          const items = unusedRes.value
            .filter((dto) => {
              const expireAtMs = new Date(dto.expireAt).getTime()
              const isExpiredByTime =
                !Number.isNaN(expireAtMs) &&
                Date.now() > expireAtMs &&
                dto.status !== 'USED' &&
                dto.status !== 'CANCELED' &&
                dto.status !== 'SUSPENDED'
              return !isExpiredByTime
            })
            .map(toUnusedCoupon)
          setUnusedCoupons(items)
        }
        else setUnusedCoupons([])
        if (couponHistoryRes.status === 'fulfilled') setCouponHistory(couponHistoryRes.value.map(toCouponHistoryItem))
        else setCouponHistory([])
      } catch (e) {
        if (!cancelled) setRewardRecentError(e instanceof Error ? e.message : '적립 내역을 불러올 수 없습니다.')
      } finally {
        if (!cancelled) setRewardRecentLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [member?.customerCode])

  useEffect(() => {
    if (membershipsError) showToast(membershipsError, 'error')
  }, [membershipsError, showToast])

  useEffect(() => {
    if (rewardRecentError) showToast(rewardRecentError, 'error')
  }, [rewardRecentError, showToast])

  if (!member) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <button type="button" className={styles.backButton} onClick={goBack} aria-label="뒤로 가기">
              ←
            </button>
            <h1 className={styles.pageTitle}>회원 정보</h1>
          </div>
        </header>
        <main className={styles.mainContainer}>
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>👤</div>
            {memberLoading ? '회원 정보를 불러오는 중입니다.' : '회원을 조회해 주세요.'}
            <button type="button" className={styles.moreButton} onClick={goBack} style={{ marginTop: 16, maxWidth: 280 }}>
              적립 대시보드로 이동
            </button>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button type="button" className={styles.backButton} onClick={goBack} aria-label="뒤로 가기">
            ←
          </button>
          <h1 className={styles.pageTitle}>회원 정보</h1>
        </div>
      </header>

      <main className={styles.mainContainer}>
        <section className={styles.memberInfoCard}>
          <div className={styles.memberHeader}>
            <div className={styles.memberDetails}>
              <div className={styles.memberName}>{member.nickname}</div>
              <div className={styles.memberBarcode}>{member.customerCode}</div>
            </div>
          </div>

          {isBlocked && (
            <div className={`${styles.pointsCondition} ${styles.blockedNotice}`}>
              <strong>차단된 회원입니다</strong>
              <br />
              적립·사용이 불가합니다. 관리자에게 문의해 주세요.
            </div>
          )}

          {!isBlocked && searchStatus === 'NOT_SHOP_CUSTOMER' && (
            <div className={styles.pointsCondition}>
              <strong>아직 매장 회원이 아닙니다</strong>
              <br />
              스탬프 또는 포인트 적립 시 매장 회원으로 자동 가입됩니다.
            </div>
          )}

          {(isBlocked || searchStatus !== 'NOT_SHOP_CUSTOMER') && (
            <div className={styles.statusGrid}>
              <div className={styles.statusItem}>
                <div className={styles.statusLabel}>미사용 스탬프</div>
                <div className={`${styles.statusValue} ${styles.statusValueStamps}`}>{member.stampCount}개</div>
              </div>
              <div className={styles.statusItem}>
                <div className={styles.statusLabel}>적립된 포인트</div>
                <div className={`${styles.statusValue} ${styles.statusValuePrimary}`}>{member.pointAmount.toLocaleString()}P</div>
              </div>
              <div className={styles.statusItem}>
                <div className={styles.statusLabel}>미사용 쿠폰</div>
                <div className={`${styles.statusValue} ${styles.statusValueCoupons}`}>{member.couponCount}개</div>
              </div>
            </div>
          )}

          {!isBlocked && searchStatus !== 'NOT_SHOP_CUSTOMER' && (
          <div className={styles.pointsCondition}>
            <strong>적립 조건</strong>
            <br />
            {pointsConditionText.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </div>
          )}
        </section>

        {isBlocked ? null : membershipsLoading && !memberships?.length ? (
          <section className={styles.sectionCard}>
            <h2 className={styles.sectionTitle}>포인트 적립</h2>
            <div className={styles.emptyState}>멤버십 정보를 불러오는 중입니다.</div>
          </section>
        ) : membershipsError && !memberships?.length ? (
          <section className={styles.sectionCard}>
            <h2 className={styles.sectionTitle}>포인트 적립</h2>
            <div className={styles.emptyState}>{membershipsError}</div>
          </section>
        ) : memberships?.length ? (
          memberships.map((m) => {
            const mode = cardModes[m.membershipCode] ?? 'earn'
            const showStamp = m.rewardType === 'STAMP' || m.rewardType === 'BOTH'
            const showPoint = m.rewardType === 'POINT' || m.rewardType === 'BOTH'
            const stampCancelEarnKey = `${m.membershipCode}-STAMP-earn`
            const pointCancelEarnKey = `${m.membershipCode}-POINT-earn`
            const pointCancelUseKey = `${m.membershipCode}-POINT-use`
            return (
            <section key={m.membershipCode} className={styles.sectionCard}>
              <div className={styles.sectionTitleRow}>
                <h2 className={styles.sectionTitleInRow}>
                  {m.title} {mode === 'earn' ? '적립' : '취소'}
                </h2>
                <div className={styles.modeToggle} role="group" aria-label="적립/취소 모드">
                  <button
                    type="button"
                    className={`${styles.modeToggleButton} ${mode === 'earn' ? styles.modeToggleButtonActive : ''}`}
                    onClick={() => setCardMode(m.membershipCode, 'earn')}
                    aria-pressed={mode === 'earn'}
                  >
                    적립
                  </button>
                  <button
                    type="button"
                    className={`${styles.modeToggleButton} ${mode === 'cancel' ? styles.modeToggleButtonActive : ''}`}
                    onClick={() => setCardMode(m.membershipCode, 'cancel')}
                    aria-pressed={mode === 'cancel'}
                  >
                    취소
                  </button>
                </div>
              </div>

              {mode === 'earn' ? (
                <>
                  {showStamp && (
                    <div className={styles.stampSection}>
                      <div className={styles.stampControl}>
                        <span className={styles.stampLabel}>스탬프 적립</span>
                        <div className={styles.stampInputGroup}>
                          <button type="button" className={styles.stampButton} onClick={decreaseStamp} aria-label="스탬프 감소">−</button>
                          <input type="text" className={styles.stampInput} value={stampCount} readOnly aria-label="스탬프 개수" />
                          <button type="button" className={styles.stampButton} onClick={increaseStamp} aria-label="스탬프 증가">+</button>
                        </div>
                        <button
                          type="button"
                          className={styles.actionButton}
                          onClick={() => void earnStamp()}
                          disabled={stampEarnLoading}
                        >
                          {stampEarnLoading ? '적립 중...' : '스탬프 적립'}
                        </button>
                      </div>
                    </div>
                  )}
                  {showPoint && (
                    <>
                      <div className={styles.pointSection}>
                        <div className={styles.inputGroup}>
                          <div className={styles.inputWrapper}>
                            <label className={styles.inputLabel} htmlFor={`paymentAmount-${m.membershipCode}`}>결제 금액</label>
                            <input id={`paymentAmount-${m.membershipCode}`} type="text" className={styles.amountInput} placeholder="0" value={paymentAmount} onChange={handlePaymentAmountChange} />
                          </div>
                          <button
                            type="button"
                            className={styles.actionButton}
                            onClick={() => void earnPoints()}
                            disabled={pointEarnLoading}
                          >
                            {pointEarnLoading ? '적립 중...' : '포인트 적립'}
                          </button>
                        </div>
                      </div>
                      <div className={styles.pointSection}>
                        <div className={styles.inputGroup}>
                          <div className={styles.inputWrapper}>
                            <label className={styles.inputLabel} htmlFor={`usePoints-${m.membershipCode}`}>사용 포인트</label>
                            <input id={`usePoints-${m.membershipCode}`} type="text" className={styles.amountInput} placeholder="0" value={usePoints} onChange={handleUsePointsChange} />
                          </div>
                          <button
                            type="button"
                            className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
                            onClick={() => void usePointsAction()}
                            disabled={pointUseLoading}
                          >
                            {pointUseLoading ? '처리 중...' : '포인트 사용'}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  {showStamp && (
                    <div className={styles.stampSection}>
                      <div className={styles.cancelHint}>
                        보유 스탬프 {member?.stampCount ?? 0}개
                      </div>
                      <div className={styles.stampControl}>
                        <span className={styles.stampLabel}>스탬프 수량</span>
                        <div className={styles.stampInputGroup}>
                          <button type="button" className={styles.stampButton} onClick={decreaseCancelStamp} aria-label="취소 스탬프 감소">−</button>
                          <input type="text" className={styles.stampInput} value={cancelStampCount} readOnly aria-label="취소 스탬프 개수" />
                          <button type="button" className={styles.stampButton} onClick={increaseCancelStamp} aria-label="취소 스탬프 증가">+</button>
                        </div>
                        <button
                          type="button"
                          className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
                          onClick={() => void cancelRewardQuantity(m.membershipCode, 'STAMP', 'earn')}
                          disabled={cancelLoadingKey !== null}
                        >
                          {cancelLoadingKey === stampCancelEarnKey ? '처리 중...' : '적립취소'}
                        </button>
                      </div>
                    </div>
                  )}
                  {showPoint && (
                    <div className={styles.pointSection}>
                      <div className={styles.cancelHint}>
                        보유 포인트 {(member?.pointAmount ?? 0).toLocaleString()}P
                      </div>
                      <div className={styles.inputGroup}>
                        <div className={styles.inputWrapper}>
                          <label className={styles.inputLabel} htmlFor={`cancelPoints-${m.membershipCode}`}>취소 포인트</label>
                          <input
                            id={`cancelPoints-${m.membershipCode}`}
                            type="text"
                            className={styles.amountInput}
                            placeholder="0"
                            value={cancelPoints}
                            onChange={handleCancelPointsChange}
                          />
                        </div>
                        <div className={styles.cancelButtonGroup}>
                          <button
                            type="button"
                            className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
                            onClick={() => void cancelRewardQuantity(m.membershipCode, 'POINT', 'earn')}
                            disabled={cancelLoadingKey !== null}
                          >
                            {cancelLoadingKey === pointCancelEarnKey ? '처리 중...' : '적립취소'}
                          </button>
                          <button
                            type="button"
                            className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
                            onClick={() => void cancelRewardQuantity(m.membershipCode, 'POINT', 'use')}
                            disabled={cancelLoadingKey !== null}
                          >
                            {cancelLoadingKey === pointCancelUseKey ? '처리 중...' : '사용취소'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
            )
          })
        ) : (
          <section className={styles.sectionCard}>
            <h2 className={styles.sectionTitle}>적립</h2>
            <div className={styles.emptyState}>현재 매장은 적립을 지원하지 않습니다.</div>
          </section>
        )}

        <section className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>미사용 쿠폰</h2>
          <div className={styles.couponList}>
            {unusedCoupons.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>🎟️</div>
                미사용 쿠폰이 없습니다.
              </div>
            ) : (
              unusedCoupons.map((coupon) => (
                <div key={coupon.id} className={styles.couponItem}>
                  <div className={styles.couponHeader}>
                    <div className={styles.couponName}>{coupon.name}</div>
                    <button
                      type="button"
                      className={styles.useCouponButton}
                      onClick={() => void useCoupon(coupon.code)}
                      disabled={isBlocked || usingCouponCode !== null}
                    >
                      {usingCouponCode === coupon.code ? '처리 중...' : '쿠폰 사용'}
                    </button>
                  </div>
                  <div className={styles.couponInfo}>
                    <div className={styles.couponDate}>
                      <span>발급일: {coupon.issuedAt}</span>
                    </div>
                    <div className={styles.couponDate}>
                      <span>만료일: {coupon.expiresAt}</span>
                    </div>
                    <div className={styles.remainingDays}>D-{coupon.remainingDays}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <section className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>적립 내역</h2>
          {rewardRecentLoading && rewardRecent.length === 0 ? (
            <div className={styles.emptyState}>적립 내역을 불러오는 중입니다.</div>
          ) : rewardRecentError && rewardRecent.length === 0 ? (
            <div className={styles.emptyState}>{rewardRecentError}</div>
          ) : rewardRecent.length === 0 ? (
            <div className={styles.emptyState}>적립 내역이 없습니다.</div>
          ) : (
            <>
              <div className={styles.historyList}>
                {rewardRecent.map((item) => {
                  const amountLabel = formatRewardAmountDisplay(
                    item.rewardAmount,
                    item.rewardType,
                    item.rewardStatus
                  )
                  const amountClass =
                    item.rewardAmount < 0
                      ? styles.historyAmountNegative
                      : styles.historyAmountPositive
                  return (
                    <div key={item.id} className={styles.historyItem}>
                      <div className={styles.historyInfo}>
                        <div className={styles.historyDate}>{item.rewardAt}</div>
                        <div className={styles.historyDescription}>
                          {item.rewardType === 'STAMP' ? '스탬프' : '포인트'} · {REWARD_STATUS_LABEL[item.rewardStatus]}
                        </div>
                      </div>
                      <div className={`${styles.historyAmount} ${amountClass}`}>
                        {amountLabel}
                      </div>
                    </div>
                  )
                })}
              </div>
              <button type="button" className={styles.moreButton} onClick={showMorePointHistory}>
                적립 내역 더보기
              </button>
            </>
          )}
        </section>

        <section className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>쿠폰 내역</h2>
          <div className={styles.historyList}>
            {couponHistory.map((item) => (
              <div key={item.id} className={styles.historyItem}>
                <div className={styles.historyInfo}>
                  <div className={styles.historyDate}>{item.date}</div>
                  <div className={styles.historyDescription}>{item.description}</div>
                </div>
                <div
                  className={`${styles.historyStatus} ${item.status === 'issued' ? styles.historyStatusIssued : styles.historyStatusUsed}`}
                >
                  {item.status === 'issued' ? '발급완료' : '사용완료'}
                </div>
              </div>
            ))}
          </div>
          <button type="button" className={styles.moreButton} onClick={showMoreCouponHistory}>
            쿠폰 내역 더보기
          </button>
        </section>
      </main>

      {toast && (
        <div
          className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}
          role="alert"
        >
          {toast.message}
        </div>
      )}
    </div>
  )
}
